import { Button } from '@mantine/core';
import {
  IconAlertTriangle,
  IconBox,
  IconFileText,
  IconPlus,
  IconShoppingCart,
  IconTool,
} from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime, formatDay } from '../../format.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import table from '../materials/Materials.module.css';
import { DefectPriorityBadge, DefectStageBadge } from './DefectParts.js';
import classes from './Defects.module.css';
import { equipmentLabel } from './routes.js';
import type {
  DefectApprovalStep,
  DefectDetail,
  DefectHseLink,
  DefectRequisitionLink,
  DefectWorkOrderLink,
} from './types.js';
import { sideSteps, type WorkflowStep, workflowSteps } from './workflow.js';

/**
 * LGS-17 ekranının parçaları (ADR 0096 §1; `ariza-ekran.jpeg`): bölüm kartı (Part A/B/C, SMS),
 * imza satırı, sağ panel (İlgili Kayıtlar, Durum, Basamaklar, Hızlı İşlemler) ve sekme tabloları.
 */

type Tone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

/** Bağlı kaydın durum tonu: tamam yeşil, açık turuncu, iptal gri. */
export function linkTone(status: string): Tone {
  if (['reported', 'closed', 'approved', 'po_issued'].includes(status)) return 'success';
  if (['cancelled', 'draft'].includes(status)) return 'neutral';
  if (status === 'returned') return 'danger';
  return 'warning';
}

/** Bölüm kartı: numara, başlık / ipucu, sağda durum ve tarih. */
export function PartCard({
  id,
  part,
  no,
  title,
  hint,
  done,
  at,
  children,
}: {
  id: string;
  part: 'a' | 'b' | 'c' | 'sms';
  no: number;
  title: string;
  hint: string;
  done: boolean;
  at?: string | null;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <section id={id} className={classes.part} data-part={part} aria-label={title}>
      <header className={classes.partHead}>
        <span className={classes.partNo} aria-hidden>
          {no}
        </span>
        <h2 className={classes.partTitle}>{title}</h2>
        <span className={classes.partHint}>/ {hint}</span>
        <span className={classes.partState}>
          <StatusBadge tone={done ? 'success' : 'neutral'}>
            {t(done ? 'defects.lgs.parts.completed' : 'defects.lgs.parts.waiting')}
          </StatusBadge>
          {at ? <span>{at}</span> : null}
        </span>
      </header>
      <div className={classes.partBody}>{children}</div>
    </section>
  );
}

/**
 * SMS bölümünün imza satırı (A2): mürettebat (bildiren ve kapanışa gönderen), kaptan (gemideki
 * onay adımı), işletme onayı (ofis adımı). İmza çizilmez; kişi, rol ve zaman onay kaydından.
 */
export function SignRow({ detail }: { detail: DefectDetail }) {
  const { t } = useTranslation();
  const f = (key: string) => t(`defects.lgs.fields.${key}`);
  const when = (at: string | null | undefined) =>
    at ? formatDateTime(at, detail.tzOffsetMin) : '—';
  const steps = detail.signatures.steps;
  const cell = (label: string, step: DefectApprovalStep | undefined, fallback?: ReactNode) => (
    <div className={classes.signCell}>
      <span className={classes.muted}>{label}</span>
      {step?.signature ? (
        <>
          <strong>
            {step.signature.name ?? '—'}
            {step.signature.roleName ? ` · ${step.signature.roleName}` : ''}
          </strong>
          <span>
            {f('date')}: {when(step.signature.at)}
            {step.decision === 'returned'
              ? ` (${t('defects.signatures.returned', { at: '' })})`
              : ''}
          </span>
        </>
      ) : (
        (fallback ?? (
          <span className={classes.muted}>
            {detail.signatures.submission
              ? step
                ? `${t('defects.signatures.pending')}${step.roleName ? ` · ${step.roleName}` : ''}`
                : t('defects.signatures.notRequired')
              : t('defects.signatures.notSubmitted')}
          </span>
        ))
      )}
    </div>
  );
  const submission = detail.signatures.submission;
  return (
    <div className={classes.signRow}>
      <div className={classes.signCell}>
        <span className={classes.muted}>{f('crewSignature')}</span>
        <strong>
          {detail.reportedBy.name ?? '—'}
          {detail.reportedBy.roleName ? ` · ${detail.reportedBy.roleName}` : ''}
        </strong>
        <span>
          {f('date')}: {when(detail.reportedAt)}
        </span>
        {submission ? (
          <span className={classes.muted}>
            {t('defects.signatures.submitted', {
              at: `${when(submission.at)} · ${submission.name ?? '—'}`,
            })}
          </span>
        ) : null}
      </div>
      {cell(
        f('captainSignature'),
        steps.find((s) => !s.office),
      )}
      {cell(
        f('managementApproval'),
        steps.find((s) => s.office),
      )}
    </div>
  );
}

export interface QuickAction {
  key: string;
  label: string;
  icon: ReactNode;
  onClick: () => void;
  wide?: boolean;
  disabled?: boolean;
  loading?: boolean;
}

/** Sağ panel (`ariza-ekran.jpeg`): İlgili Kayıtlar, Durum, Basamaklar, Hızlı İşlemler. */
export function SidePanel({
  detail,
  quick,
  onWorkOrder,
  onNonConformity,
  onRequisition,
}: {
  detail: DefectDetail;
  quick: QuickAction[];
  onWorkOrder?: ((id: string) => void) | undefined;
  onNonConformity?: ((link: DefectHseLink) => void) | undefined;
  onRequisition?: ((id: string) => void) | undefined;
}) {
  const { t } = useTranslation();
  const l = (key: string, params: Record<string, unknown> = {}) => t(`defects.lgs.${key}`, params);
  const links = detail.links;
  const item = (
    key: string,
    kind: string,
    label: ReactNode,
    status: { tone: Tone; text: string } | null,
  ) => (
    <li key={key} className={classes.sideItem}>
      <span>
        <span className={classes.sideKind}>{kind}</span>
        {label}
      </span>
      {status ? <StatusBadge tone={status.tone}>{status.text}</StatusBadge> : <span />}
    </li>
  );
  const linkButton = (text: string, onClick?: () => void) =>
    onClick ? (
      <button type="button" className={classes.inlineLink} onClick={onClick}>
        {text}
      </button>
    ) : (
      <span>{text}</span>
    );
  const related: ReactNode[] = [
    ...(links?.workOrders ?? []).map((w) =>
      item(
        `wo-${w.id}`,
        l('side.kinds.workOrder'),
        linkButton(w.woNo, onWorkOrder ? () => onWorkOrder(w.id) : undefined),
        { tone: linkTone(w.status), text: l(`workOrders.statuses.${w.status}`) },
      ),
    ),
    ...(links?.nonConformities ?? []).map((n) =>
      item(
        `nc-${n.id}`,
        l('side.kinds.nonConformity'),
        linkButton(n.reportNo, onNonConformity ? () => onNonConformity(n) : undefined),
        {
          tone: n.status === 'closed' ? 'success' : 'warning',
          text: t(`hse.statuses.${n.status}`, { defaultValue: n.status }),
        },
      ),
    ),
    ...(links?.requisitions ?? []).map((r) =>
      item(
        `pr-${r.id}`,
        l('side.kinds.requisition'),
        linkButton(r.requisitionNo, onRequisition ? () => onRequisition(r.id) : undefined),
        {
          tone: linkTone(r.status),
          text: l(`material.reqStatuses.${r.status}`, { defaultValue: r.status }),
        },
      ),
    ),
    ...(detail.equipment || detail.systemName
      ? [
          item(
            'equipment',
            l('side.kinds.equipment'),
            <span className={classes.muted}>{equipmentLabel(detail)}</span>,
            null,
          ),
        ]
      : []),
  ];
  const steps = sideSteps(detail);
  return (
    <aside className={classes.lgsSide}>
      <Panel title={l('side.related')}>
        {related.length === 0 ? (
          <p className={classes.muted}>{l('side.relatedEmpty')}</p>
        ) : (
          <ul className={classes.sideList}>{related}</ul>
        )}
      </Panel>
      <Panel title={l('side.status')}>
        <dl className={classes.sideRows}>
          <dt>{l('side.current')}</dt>
          <dd>
            <DefectStageBadge stage={detail.stage} awaitingParts={detail.awaitingParts} />
          </dd>
          <dt>{l('side.priority')}</dt>
          <dd>
            <DefectPriorityBadge priority={detail.priority} />
          </dd>
          <dt>{l('side.category')}</dt>
          <dd>
            {detail.equipment?.category ??
              (detail.department ? t(`defects.departments.${detail.department}`) : '—')}
          </dd>
          <dt>{l('side.createdBy')}</dt>
          <dd>{detail.reportedBy.name ?? '—'}</dd>
          <dt>{l('side.createdAt')}</dt>
          <dd>{formatDateTime(detail.reportedAt, detail.tzOffsetMin)}</dd>
          <dt>{l('side.updatedAt')}</dt>
          <dd>
            {formatDateTime(String(detail.updatedAt ?? detail.reportedAt), detail.tzOffsetMin)}
          </dd>
        </dl>
      </Panel>
      <Panel title={l('side.steps')}>
        <ol className={classes.flow}>
          {steps.map((step, index) => (
            <FlowItem
              key={step.key}
              step={step}
              no={index + 1}
              tzOffsetMin={detail.tzOffsetMin ?? 0}
            />
          ))}
        </ol>
      </Panel>
      <Panel title={l('side.quick')}>
        {quick.length === 0 ? (
          <p className={classes.muted}>{l('side.quickNone')}</p>
        ) : (
          <div className={classes.quick}>
            {quick.map((action) => (
              <Button
                key={action.key}
                variant="outline"
                size="xs"
                leftSection={action.icon}
                className={action.wide ? classes.quickWide : undefined}
                onClick={action.onClick}
                disabled={action.disabled ?? false}
                loading={action.loading ?? false}
              >
                {action.label}
              </Button>
            ))}
          </div>
        )}
      </Panel>
    </aside>
  );
}

function stepWhen(step: WorkflowStep, tzOffsetMin: number): string | null {
  if (!step.at) return null;
  return step.at.length === 10 ? formatDay(step.at) : formatDateTime(step.at, tzOffsetMin);
}

function FlowItem({
  step,
  no,
  tzOffsetMin,
}: {
  step: WorkflowStep;
  no: number;
  tzOffsetMin: number;
}) {
  const { t } = useTranslation();
  const when = stepWhen(step, tzOffsetMin);
  return (
    <li className={classes.flowItem} data-state={step.state}>
      <span className={classes.flowNo}>{no}</span>
      <span>
        <strong>{t(`defects.lgs.workflow.steps.${step.key}`)}</strong>
        <span className={classes.muted}>
          {step.state === 'done' && (when || step.by)
            ? [when, step.by].filter(Boolean).join(' – ')
            : t(`defects.lgs.workflow.states.${step.state}`)}
        </span>
      </span>
    </li>
  );
}

/** Workflow / Geçmiş sekmesinin basamak tablosu (A1, A10). */
export function WorkflowTable({ detail }: { detail: DefectDetail }) {
  const { t } = useTranslation();
  const w = (key: string) => t(`defects.lgs.workflow.${key}`);
  const steps = workflowSteps(detail);
  return (
    <section
      className={table.tableWrap}
      aria-label={w('table')}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
      tabIndex={0}
    >
      <table className={`${table.table} ${classes.table}`}>
        <thead>
          <tr>
            {(['no', 'stage', 'step', 'owner', 'state', 'when'] as const).map((c) => (
              <th key={c} scope="col">
                {w(`columns.${c}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {steps.map((step, index) => (
            <tr key={step.key}>
              <td>{index + 1}</td>
              <td className={classes.nowrap}>{t(`defects.stages.${step.stage}`)}</td>
              <td>{w(`steps.${step.key}`)}</td>
              <td>{w(`owners.${step.owner}`)}</td>
              <td>
                <StatusBadge
                  tone={
                    step.state === 'done'
                      ? 'success'
                      : step.state === 'current'
                        ? 'info'
                        : 'neutral'
                  }
                >
                  {w(`states.${step.state}`)}
                </StatusBadge>
              </td>
              <td>
                {[stepWhen(step, detail.tzOffsetMin ?? 0), step.by].filter(Boolean).join(' – ') ||
                  '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/** Basit bağlı kayıt tablosu (iş emirleri, uygunsuzluklar, talepler). */
function LinkTable<T extends { id: string }>({
  label,
  columns,
  rows,
  empty,
}: {
  label: string;
  columns: string[];
  rows: { item: T; cells: ReactNode[] }[];
  empty: string;
}) {
  if (rows.length === 0) return <p className={classes.muted}>{empty}</p>;
  return (
    <section
      className={table.tableWrap}
      aria-label={label}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
      tabIndex={0}
    >
      <table className={`${table.table} ${classes.table}`}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c} scope="col">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(({ item, cells }) => (
            <tr key={item.id}>
              {cells.map((cell, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: sütun sırası sabit
                <td key={index}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

export function WorkOrdersTable({
  items,
  onOpen,
}: {
  items: DefectWorkOrderLink[];
  onOpen?: ((id: string) => void) | undefined;
}) {
  const { t } = useTranslation();
  const w = (key: string, params: Record<string, unknown> = {}) =>
    t(`defects.lgs.workOrders.${key}`, params);
  return (
    <LinkTable
      label={w('table')}
      empty={w('empty')}
      columns={[w('columns.no'), w('columns.title'), w('columns.kind'), w('columns.status')]}
      rows={items.map((item) => ({
        item,
        cells: [
          onOpen ? (
            <button
              key="link"
              type="button"
              className={classes.inlineLink}
              aria-label={w('open', { no: item.woNo })}
              onClick={() => onOpen(item.id)}
            >
              {item.woNo}
            </button>
          ) : (
            item.woNo
          ),
          item.title ?? '—',
          w(`kinds.${item.kind}`, { defaultValue: item.kind }),
          <StatusBadge key="s" tone={linkTone(item.status)}>
            {w(`statuses.${item.status}`, { defaultValue: item.status })}
          </StatusBadge>,
        ],
      }))}
    />
  );
}

export function NonConformityTable({
  items,
  onOpen,
}: {
  items: DefectHseLink[];
  onOpen?: ((link: DefectHseLink) => void) | undefined;
}) {
  const { t } = useTranslation();
  const n = (key: string, params: Record<string, unknown> = {}) =>
    t(`defects.lgs.nc.${key}`, params);
  return (
    <LinkTable
      label={n('table')}
      empty={n('empty')}
      columns={[n('columns.no'), n('columns.type'), n('columns.title'), n('columns.status')]}
      rows={items.map((item) => ({
        item,
        cells: [
          onOpen ? (
            <button
              key="link"
              type="button"
              className={classes.inlineLink}
              aria-label={n('open', { no: item.reportNo })}
              onClick={() => onOpen(item)}
            >
              {item.reportNo}
            </button>
          ) : (
            item.reportNo
          ),
          t(`hse.types.${item.reportType}`, { defaultValue: item.reportType }),
          item.title ?? '—',
          <StatusBadge key="s" tone={item.status === 'closed' ? 'success' : 'warning'}>
            {t(`hse.statuses.${item.status}`, { defaultValue: item.status })}
          </StatusBadge>,
        ],
      }))}
    />
  );
}

export function MaterialTables({
  detail,
  onRequisition,
}: {
  detail: DefectDetail;
  onRequisition?: ((id: string) => void) | undefined;
}) {
  const { t } = useTranslation();
  const m = (key: string, params: Record<string, unknown> = {}) =>
    t(`defects.lgs.material.${key}`, params);
  const materials = detail.links?.materials ?? [];
  const requisitions: DefectRequisitionLink[] = detail.links?.requisitions ?? [];
  return (
    <>
      <Panel title={m('used')} flush>
        <div className={classes.cardTable}>
          <LinkTable
            label={m('table')}
            empty={m('empty')}
            columns={[
              m('columns.material'),
              m('columns.quantity'),
              m('columns.date'),
              m('columns.via'),
            ]}
            rows={materials.map((item) => ({
              item,
              cells: [
                item.name,
                `${item.quantity}${item.unit ? ` ${item.unit}` : ''}`,
                item.localDate ? formatDay(item.localDate) : '—',
                item.via === 'defect'
                  ? m('viaDefect')
                  : `${m('viaWorkOrder', { wo: item.workOrderNo ?? '—' })}${item.fromStock ? '' : ` (${m('notFromStock')})`}`,
              ],
            }))}
          />
        </div>
      </Panel>
      <Panel title={m('requisitions')} flush>
        <div className={classes.cardTable}>
          <LinkTable
            label={m('reqTable')}
            empty={m('reqEmpty')}
            columns={[m('reqColumns.no'), m('reqColumns.date'), m('reqColumns.status')]}
            rows={requisitions.map((item) => ({
              item,
              cells: [
                onRequisition ? (
                  <button
                    key="link"
                    type="button"
                    className={classes.inlineLink}
                    aria-label={m('open', { no: item.requisitionNo })}
                    onClick={() => onRequisition(item.id)}
                  >
                    {item.requisitionNo}
                  </button>
                ) : (
                  item.requisitionNo
                ),
                formatDay(item.requestDate),
                <StatusBadge key="s" tone={linkTone(item.status)}>
                  {m(`reqStatuses.${item.status}`, { defaultValue: item.status })}
                </StatusBadge>,
              ],
            }))}
          />
        </div>
      </Panel>
    </>
  );
}

export const QUICK_ICONS = {
  workOrder: <IconTool size={14} />,
  nc: <IconAlertTriangle size={14} />,
  requisition: <IconShoppingCart size={14} />,
  stock: <IconBox size={14} />,
  plus: <IconPlus size={14} />,
  document: <IconFileText size={14} />,
};
